import React, { useState, useEffect } from 'react';
import './AllMembers.css';
import teamData from '../data/team.json';
import { useContent } from '../contexts/ContentContext';

type Member = {
  id: number;
  name: string;
  role?: string;
  photo: string;
};

type Batch = {
  year: string;
  name: string;
  description: string;
  icon: string;
  members: Member[];
};

const AllMembers = () => {
  const { getSection } = useContent();
  const data = getSection<typeof teamData>('team', teamData);
  const batches = data.teamMembers.batches as Batch[];
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  useEffect(() => {
    // Check if there's a hash in the URL to scroll to a specific batch
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const openMemberModal = (member: Member) => {
    setSelectedMember(member);
  };

  const closeMemberModal = () => {
    setSelectedMember(null);
  };

  const scrollToBatch = (batchYear: string) => {
    const element = document.getElementById(`batch-${batchYear}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="all-members-page">
    <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">All Team Members</h1>
          <p className="page-subtitle">
            Meet every member of the ASIMO Technical Club family
          </p>
        </div>

        {/* Batch Navigation */}
        <div className="batch-navigation">
          {batches.map((batch) => (
            <button
              key={batch.year}
              className="nav-batch-btn"
              onClick={() => scrollToBatch(batch.year)}
            >
              <i className={batch.icon}></i>
              {batch.name}
            </button>
          ))}
        </div>

        {/* All Batches */}
        {batches.map((batch) => (
          <div key={batch.year} id={`batch-${batch.year}`} className="batch-section">
            <div className="batch-header-section">
              <div className="batch-title-container">
                <div className="batch-icon-large">
                  <i className={batch.icon}></i>
                </div>
                <div className="batch-info-section">
                  <h2 className="batch-title-large">{batch.name}</h2>
                  <div className="batch-year-large">{batch.year}</div>
                  <p className="batch-description-large">{batch.description}</p>
                </div>
              </div>
              <div className="batch-stats-large">
                <div className="stat-item-large">
                  <span className="stat-number-large">{batch.members.length}</span>
                  <span className="stat-label-large">Members</span>
                </div>
                <div className="stat-item-large">
                  <span className="stat-number-large">{batch.year === '2023' ? 'Current' : 'Alumni'}</span>
                  <span className="stat-label-large">Status</span>
                </div>
              </div>
            </div>

            <div className="members-grid-large">
              {batch.members.map((member) => (
                <div key={member.id} className="member-card-large" onClick={() => openMemberModal(member)}>
                  <div className="member-photo-large">
                    <img 
                      src={member.photo} 
                      alt={member.name}
                      className="member-image-large"
                      onError={(e) => {
                        const img = e.currentTarget;
                        img.style.display = 'none';
                        const fallback = img.nextElementSibling as HTMLElement | null;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                    <div className="photo-placeholder-large" style={{display: 'none'}}>
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="member-overlay-large">
                      <span>View Profile</span>
                    </div>
                  </div>
                  <div className="member-info-large">
                    <h4 className="member-name-large">{member.name}</h4>
                    <p className="member-role-large">{member.role || 'Member'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="back-to-home">
          <button 
            className="btn-back"
            onClick={() => window.history.back()}
          >
            ← Back to Home
          </button>
        </div>
      </div>

      {/* Member Modal */}
      {selectedMember && (
        <div className="member-modal-overlay" onClick={closeMemberModal}>
          <div className="member-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={closeMemberModal}>×</button>
            
            <div className="modal-header">
              <div className="modal-photo">
                <img 
                  src={selectedMember.photo} 
                  alt={selectedMember.name}
                  className="modal-image"
                  onError={(e) => {
                    const img = e.currentTarget;
                    img.style.display = 'none';
                    const fallback = img.nextElementSibling as HTMLElement | null;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
                <div className="photo-placeholder-modal" style={{display: 'none'}}>
                  {selectedMember.name.split(' ').map(n => n[0]).join('')}
                </div>
              </div>
              <div className="modal-info">
                <h2>{selectedMember.name}</h2>
                <p className="modal-role">{selectedMember.role || 'ASIMO Club Member'}</p>
              </div>
            </div>
            
            <div className="modal-content">
              <div className="modal-section">
                <h3>Member of ASIMO Technical Club</h3>
                <p>Contributing to the technical excellence at Government Engineering College Samastipur</p>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
  );
};

export default AllMembers;